/**
 * The Growth Intelligence filter bar (Figma 4377:6702): pills that open a menu each, and Reset filters.
 * Choices live in the URL's search params, so a filtered view survives navigation and can be shared
 * (QA-14). The dashboard's Recommended actions and All insights (3.8) use the same bar.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useSearchParams } from "react-router";

import { Button, cx, Icon, type IconName } from "@/components/ui";
import { jurisdictionName, sinceDays, SOURCE_TYPE_LABEL } from "@/lib/growth";
import type { GrowthNiche, GrowthNicheCatalogue } from "@/lib/types";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSpec {
  /** The search param it's kept in. */
  key: string;
  label: string;
  icon: IconName;
  options: FilterOption[];
  /** Several options at once (a list param), or exactly one. */
  multiple?: boolean;
  /** For a single choice: what applies when the URL says nothing ("Last 30 days"). */
  initial?: string;
  /** The pill shows only the chosen option ("All"), not "Label: option". */
  bare?: boolean;
}

// ── the filters the Growth Intelligence pages share ─────────────────────────

export const dateFilter: FilterSpec = {
  key: "date",
  label: "Date",
  icon: "calendar",
  initial: "30d",
  options: [
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "all", label: "Any time" },
  ],
};

const DATE_DAYS: Record<string, number | null> = { "7d": 7, "30d": 30, "90d": 90, all: null };

/** The Date filter's choice as the API's `since` (none for "Any time"). */
export function dateSince(choice: string | undefined): string | undefined {
  return sinceDays(DATE_DAYS[choice ?? "30d"] ?? null);
}

/** The feed, or the person's saved or dismissed insights (All insights, Figma 4342:4497). */
export const insightStatusFilter: FilterSpec = {
  key: "status",
  label: "Status",
  icon: "activity-heart",
  initial: "open",
  options: [
    { value: "open", label: "Open" },
    { value: "saved", label: "Saved" },
    { value: "dismissed", label: "Dismissed" },
  ],
};

/** The niche's places, plus UK-wide and global developments (Figma 4585:3442). */
export function jurisdictionFilter(
  niche: GrowthNiche | undefined,
  catalogue: GrowthNicheCatalogue | undefined,
): FilterSpec {
  const ids = [...new Set([...(niche?.jurisdictions ?? []), "uk", "global"])];
  return {
    key: "jurisdiction",
    label: "Jurisdiction",
    icon: "globe",
    multiple: true,
    options: ids.map((id) => ({ value: id, label: jurisdictionName(id, catalogue) })),
  };
}

/** The niche's own topics. */
export function topicFilter(niche: GrowthNiche | undefined): FilterSpec {
  return {
    key: "topic",
    label: "Topic",
    icon: "tag",
    multiple: true,
    options: niche?.topics.map((t) => ({ value: t.id, label: t.label })) ?? [],
  };
}

export const sourceTypeFilter: FilterSpec = {
  key: "source_type",
  label: "Source type",
  icon: "filter",
  multiple: true,
  options: Object.entries(SOURCE_TYPE_LABEL).map(([value, label]) => ({ value, label })),
};

export const urgencyFilter: FilterSpec = {
  key: "urgency",
  label: "Urgency",
  icon: "bolt",
  multiple: true,
  options: [
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
  ],
};

/** Each filter's chosen values, read from the URL (the initial value when there's none). */
export function useFilterValues(specs: FilterSpec[]): Record<string, string[]> {
  const [params] = useSearchParams();
  return Object.fromEntries(
    specs.map((s) => {
      const chosen = params.getAll(s.key).filter((v) => s.options.some((o) => o.value === v));
      return [s.key, chosen.length ? chosen : s.initial ? [s.initial] : []];
    }),
  );
}

export function FilterBar({ specs, label }: { specs: FilterSpec[]; label: string }) {
  const [params, setParams] = useSearchParams();
  const values = useFilterValues(specs);
  const touched = specs.some((s) => params.has(s.key));

  function choose(spec: FilterSpec, chosen: string[]) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(spec.key);
        // the initial choice is the default, so it isn't spelled out in the URL
        if (!(chosen.length === 1 && chosen[0] === spec.initial)) {
          for (const v of chosen) next.append(spec.key, v);
        }
        return next;
      },
      { replace: true },
    );
  }

  function reset() {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const s of specs) next.delete(s.key);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2.5 px-1">
      {specs.map((spec) => (
        <FilterMenu
          key={spec.key}
          spec={spec}
          chosen={values[spec.key] ?? []}
          onChange={(chosen) => choose(spec, chosen)}
        />
      ))}
      <span className="flex-1" />
      <Button
        unstyled
        onClick={reset}
        disabledReason={touched ? undefined : "No filters are applied."}
        className="text-[12.5px] font-semibold text-brand-500 hover:text-brand-600 aria-disabled:cursor-default aria-disabled:hover:text-brand-500"
      >
        Reset filters
      </Button>
    </div>
  );
}

/** What a pill says: "Topic", "Urgency: High", "Topic: 2 selected", "Date: Last 30 days". */
function pillText(spec: FilterSpec, chosen: string[]): string {
  if (chosen.length === 0) return spec.label;
  if (chosen.length > 1) return `${spec.label}: ${chosen.length} selected`;
  const option = spec.options.find((o) => o.value === chosen[0]);
  if (!option) return spec.label;
  return spec.bare ? option.label : `${spec.label}: ${option.label}`;
}

/**
 * One pill and its menu. A menu button (WAI-ARIA): Enter, Space or Down opens it on the first option,
 * arrows move, Home/End jump, Escape or Tab closes and returns to the pill.
 */
function FilterMenu({
  spec,
  chosen,
  onChange,
}: {
  spec: FilterSpec;
  chosen: string[];
  onChange: (chosen: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    items.current[0]?.focus();
    const close = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  function shut() {
    setOpen(false);
    button.current?.focus();
  }

  function onMenuKey(event: KeyboardEvent<HTMLDivElement>) {
    const list = items.current.filter((el): el is HTMLButtonElement => el !== null);
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const move: Record<string, number> = {
      ArrowDown: (at + 1) % list.length,
      ArrowUp: (at - 1 + list.length) % list.length,
      Home: 0,
      End: list.length - 1,
    };
    if (event.key in move) {
      event.preventDefault();
      list[move[event.key]!]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      shut();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  }

  function pick(value: string) {
    if (!spec.multiple) {
      onChange([value]);
      shut();
      return;
    }
    onChange(chosen.includes(value) ? chosen.filter((v) => v !== value) : [...chosen, value]);
  }

  return (
    <div ref={wrapper} className="relative">
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-label={spec.bare ? `${spec.label}: ${pillText(spec, chosen)}` : undefined}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cx(
          "inline-flex items-center gap-1.5 rounded-full border bg-surface px-3 py-[7px] text-[12.5px] whitespace-nowrap text-fg-muted transition-colors hover:bg-muted",
          open ? "border-fg-subtle" : "border-border",
        )}
      >
        <Icon name={spec.icon} size={14} />
        {pillText(spec, chosen)}
        <Icon name="chevron-down" size={14} className={cx("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={spec.label}
          onKeyDown={onMenuKey}
          className="absolute top-full left-0 z-20 mt-1.5 flex min-w-[200px] flex-col rounded-xl border border-border bg-surface p-1.5 shadow-[0px_8px_24px_0px_rgba(112,71,46,0.14)]"
        >
          {spec.options.map((o, n) => {
            const on = chosen.includes(o.value);
            return (
              <button
                key={o.value}
                ref={(el) => {
                  items.current[n] = el;
                }}
                type="button"
                role={spec.multiple ? "menuitemcheckbox" : "menuitemradio"}
                aria-checked={on}
                onClick={() => pick(o.value)}
                className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-fg hover:bg-muted focus:bg-muted focus:outline-none"
              >
                <span
                  aria-hidden
                  className={cx(
                    "flex size-4 shrink-0 items-center justify-center border",
                    spec.multiple ? "rounded" : "rounded-full",
                    on ? "border-brand-500 bg-brand-500 text-white" : "border-border bg-surface",
                  )}
                >
                  {on &&
                    (spec.multiple ? (
                      <Icon name="check" size={10} />
                    ) : (
                      <span className="size-1.5 rounded-full bg-white" />
                    ))}
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
